import { supabase } from "../../supabaseClient";
import { createMyGwinBugReport } from "../support/gwinBugSupport";

export const PARTNER_GWIN_SUPPORT_TYPES = [
  { value: "bug_report", label: "Bug report" },
  { value: "request_help", label: "Request help" },
  { value: "account_access", label: "Account or access issue" },
  { value: "cohort_entrepreneur", label: "Cohort or entrepreneur issue" },
  { value: "feature_request", label: "Feature request" },
  { value: "other", label: "Other" },
];

const VALID_REQUEST_TYPES = new Set(
  PARTNER_GWIN_SUPPORT_TYPES.map((item) => item.value)
);

const VALID_STATUSES = new Set([
  "open",
  "acknowledged",
  "resolved",
  "closed",
]);

function cleanText(value, maxLength) {
  return String(value ?? "")
    .trim()
    .slice(0, maxLength);
}

function cleanStatus(value) {
  const status = cleanText(value, 30).toLowerCase();

  if (!status) {
    return null;
  }

  if (!VALID_STATUSES.has(status)) {
    throw new Error("Invalid support request status.");
  }

  return status;
}

function cleanRequestType(value) {
  const requestType = cleanText(value, 50).toLowerCase();

  if (!VALID_REQUEST_TYPES.has(requestType)) {
    throw new Error("Please choose a valid support request type.");
  }

  return requestType;
}

function normaliseSupportRequest(row) {
  if (!row) {
    return null;
  }

  return {
    report_id: row.report_id || "",
    request_type: row.request_type || "bug_report",
    subject: row.subject || "",
    message: row.message || "",
    page_path: row.page_path || "",
    status: row.status || "open",
    created_at: row.created_at || null,
    acknowledged_at: row.acknowledged_at || null,
    resolved_at: row.resolved_at || null,
    closed_at: row.closed_at || null,
  };
}

export function partnerGwinSupportTypeLabel(value) {
  const match = PARTNER_GWIN_SUPPORT_TYPES.find(
    (item) => item.value === value
  );

  return match?.label || "Support request";
}

export async function createMyPartnerGwinSupportRequest({
  requestType,
  subject,
  message,
  pagePath = "",
  metadata = {},
}) {
  const cleanType = cleanRequestType(requestType);
  const cleanSubject = cleanText(subject, 120);
  const cleanMessage = cleanText(message, 4000);
  const cleanPagePath = cleanText(pagePath, 500);

  if (cleanSubject.length < 3) {
    throw new Error("Please enter a subject of at least 3 characters.");
  }

  if (cleanMessage.length < 10) {
    throw new Error("Please provide a little more detail about your request.");
  }

  const safeMetadata =
    metadata && typeof metadata === "object" && !Array.isArray(metadata)
      ? metadata
      : {};

  return createMyGwinBugReport({
    subject: cleanSubject,
    message: cleanMessage,
    pagePath: cleanPagePath,
    metadata: {
      ...safeMetadata,
      source: "partner_portal",
      request_type: cleanType,
    },
  });
}

export async function fetchMyPartnerGwinSupportRequests(status = null) {
  const { data, error } = await supabase.rpc(
    "get_my_partner_gwin_support_requests",
    {
      p_status: cleanStatus(status),
    }
  );

  if (error) {
    throw error;
  }

  return (Array.isArray(data) ? data : [])
    .map(normaliseSupportRequest)
    .filter(Boolean);
}
